import { masterMark } from '@deltasuite/ui-tokens';
import { toPng } from 'html-to-image';
import { useRef, useState } from 'react';
import { DISPOSITION_LABELS, type DispositionRecord } from './types';

interface Props {
  history: readonly DispositionRecord[];
  pointsScored: number;
  pointTarget: number;
  contactsTouched: number;
  startedAt: number;
  endedAt: number;
  /** True when the sprint hit the daily target (R4). Adds a "Keep going"
   * affordance that resumes the runner on the next contact. */
  targetHit?: boolean;
  /** Called when the user wants to keep dialling past target. Only meaningful
   * when targetHit is true and there are contacts left in the queue. */
  onKeepGoing?: () => void;
  onClose: () => void;
}

function summariseDispositions(history: readonly DispositionRecord[]) {
  const counts = { telesales: 0, courtesy_call: 0, vme: 0, no_answer: 0, database_update: 0 };
  for (const r of history) counts[r.disposition] += 1;
  return counts;
}

function topThree(history: readonly DispositionRecord[]): readonly DispositionRecord[] {
  return [...history].sort((a, b) => b.points - a.points).slice(0, 3);
}

function durationLabel(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms / 1000) % 60);
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
}

export function SprintSummary(props: Props) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  // Keep-going double-click guard. The reducer's CONTINUE always advances
  // contactIndex by 1, so a fast double-click would silently skip a contact
  // before React unmounts the summary. Disable on first click.
  const [keepingGoing, setKeepingGoing] = useState(false);
  const counts = summariseDispositions(props.history);
  const top = topThree(props.history);

  const exportPng = async () => {
    if (!cardRef.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        backgroundColor: '#fafaf9',
      });
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `deltadial-sprint-${new Date(props.endedAt).toISOString().slice(0, 16)}.png`;
      a.click();
    } finally {
      setExporting(false);
    }
  };

  return (
    <main className="min-h-full px-8 py-10">
      <div className="mx-auto max-w-2xl">
        <header className="flex items-center justify-between">
          <p className="font-mono text-xs uppercase tracking-[0.18em] text-graphite-500">
            {props.targetHit ? 'Target hit' : 'Sprint complete'}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={exportPng}
              disabled={exporting}
              className="rounded-md border border-graphite-300 px-3 py-1.5 font-sans text-xs text-graphite-700 hover:bg-surface-sunk disabled:opacity-50"
            >
              {exporting ? 'Exporting…' : 'Export PNG'}
            </button>
            {props.targetHit && props.onKeepGoing ? (
              <button
                type="button"
                disabled={keepingGoing}
                onClick={() => {
                  if (keepingGoing) return;
                  setKeepingGoing(true);
                  props.onKeepGoing!();
                }}
                className="rounded-md bg-signal-amber px-3 py-1.5 font-sans text-xs font-medium text-surface hover:bg-signal-amber-hover disabled:opacity-50"
              >
                {keepingGoing ? 'Resuming…' : 'Keep going'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={props.onClose}
              className="rounded-md bg-graphite-900 px-3 py-1.5 font-sans text-xs font-medium text-surface hover:bg-graphite-700"
            >
              Done
            </button>
          </div>
        </header>

        <div
          ref={cardRef}
          className="mt-6 rounded-lg border border-graphite-100 bg-surface p-8 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <span className="font-sans text-xl leading-none text-signal-amber" aria-hidden>
              {masterMark}
            </span>
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.18em] text-graphite-500">
              DeltaDial
            </span>
          </div>

          <h1 className="mt-8 font-sans text-4xl font-semibold tracking-tight text-graphite-900">
            {props.pointsScored}{' '}
            <span className="font-sans text-base font-normal text-graphite-500">
              of {props.pointTarget} points
            </span>
          </h1>

          <dl className="mt-8 grid grid-cols-3 gap-4 font-mono text-xs">
            <Stat label="touched" value={`${props.contactsTouched}`} />
            <Stat label="duration" value={durationLabel(props.endedAt - props.startedAt)} />
            <Stat label="connect rate" value={`${connectRatePct(counts).toFixed(0)}%`} />
            <Stat label="telesales" value={`${counts.telesales}`} />
            <Stat label="courtesy" value={`${counts.courtesy_call}`} />
            <Stat label="vm&e" value={`${counts.vme}`} />
            <Stat label="no answer" value={`${counts.no_answer}`} />
            <Stat label="db update" value={`${counts.database_update}`} />
          </dl>

          {top.length > 0 ? (
            <div className="mt-8">
              <h3 className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-graphite-500">
                Top calls
              </h3>
              <ul className="mt-2 divide-y divide-graphite-100">
                {top.map((r) => (
                  <li
                    key={`${r.contactId}-${r.occurredAt}`}
                    className="flex items-baseline justify-between gap-3 py-2"
                  >
                    <span className="font-sans text-sm text-graphite-900">{r.contactName}</span>
                    <span className="font-mono text-xs text-graphite-500">
                      {DISPOSITION_LABELS[r.disposition]} · {r.points}pt
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </main>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <dt className="text-graphite-500">{label}</dt>
      <dd className={`mt-1 text-lg ${accent ? 'text-signal-amber' : 'text-graphite-900'}`}>
        {value}
      </dd>
    </div>
  );
}

function connectRatePct(counts: ReturnType<typeof summariseDispositions>): number {
  // Dials = anything where we touched the line. Conversations = real chats
  // (telesales + courtesy). Database Update counts as a connect (someone
  // told us the contact moved on) but isn't a sales conversation.
  const dials =
    counts.telesales +
    counts.courtesy_call +
    counts.vme +
    counts.no_answer +
    counts.database_update;
  const conversations = counts.telesales + counts.courtesy_call;
  if (dials === 0) return 0;
  return (conversations / dials) * 100;
}
